import type { RouteLocationRaw } from 'vue-router';
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type {
  BookingProduct,
  BookingStop,
  JobBookingCustomer,
  StopNumber,
  StopTimeObject,
} from '@/models/advancedJobBooking';

import { parseISO } from 'date-fns';
import { format as formatTz, toZonedTime } from 'date-fns-tz';

import {
  BOOKING_AM_WINDOW,
  BOOKING_DAY_END_MIN,
  BOOKING_DAY_START_MIN,
  BOOKING_PM_WINDOW,
  BOOKING_WAREHOUSE_DAY_START_MIN,
  timeZoneDefault,
} from '@/utils/PortalConstants';
import * as routeNames from '@/constants/routeNames';

const SCHEMA_VERSION = '1.0';

// Query params carrying the source job into the Advanced JobBooking form for the clone flow.
export const CLONE_JOB_ID_QUERY = 'clone_job_id';
export const CLONE_JOB_DATE_QUERY = 'clone_job_date';

/** Route that opens the Advanced JobBooking form prefilled from an existing job. */
export function buildCloneBookingRoute(
  jobId: number,
  jobDate?: string,
): RouteLocationRaw {
  return {
    name: routeNames.ADVANCED_JOB_BOOKING,
    query: {
      [CLONE_JOB_ID_QUERY]: String(jobId),
      ...(jobDate ? { [CLONE_JOB_DATE_QUERY]: jobDate } : {}),
    },
  };
}

function minutesToHms(min: number): string {
  const hours = String(Math.floor(min / 60)).padStart(2, '0');
  const minutes = String(min % 60).padStart(2, '0');
  return `${hours}:${minutes}:00`;
}

const STOP_DEFAULT_TIMES = {
  AM: {
    earliest: minutesToHms(BOOKING_AM_WINDOW.startMin),
    latest: minutesToHms(BOOKING_AM_WINDOW.endMin),
  },
  PM: {
    earliest: minutesToHms(BOOKING_PM_WINDOW.startMin),
    latest: minutesToHms(BOOKING_PM_WINDOW.endMin),
  },
  anyWarehouse: {
    earliest: minutesToHms(BOOKING_WAREHOUSE_DAY_START_MIN),
    latest: minutesToHms(BOOKING_DAY_END_MIN),
  },
  anyCustomer: {
    earliest: minutesToHms(BOOKING_DAY_START_MIN),
    latest: minutesToHms(BOOKING_DAY_END_MIN),
  },
};

const STATE_TIMEZONE_MAP: Record<string, string> = {
  NSW: 'Australia/Sydney',
  ACT: 'Australia/Sydney',
  VIC: 'Australia/Melbourne',
  QLD: 'Australia/Brisbane',
  SA: 'Australia/Adelaide',
  WA: 'Australia/Perth',
  NT: 'Australia/Darwin',
  TAS: 'Australia/Hobart',
};

/**
 * Resolve the UTC offset (e.g. `+11:00`) for a stop's state on a given date,
 * accounting for daylight saving. Falls back to the default timezone.
 */
function tzOffsetForState(state: string, date: string): string {
  const tz = STATE_TIMEZONE_MAP[state] ?? timeZoneDefault;
  const referenceDate = date ? parseISO(date) : new Date();
  return formatTz(toZonedTime(referenceDate, tz), 'xxxxx', { timeZone: tz });
}

function formatTime(time: StopTimeObject | null): string {
  if (!time) return '00:00:00';
  const hours = String(time.hours).padStart(2, '0');
  const minutes = String(time.minutes).padStart(2, '0');
  const seconds = String(time.seconds ?? 0).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
}

/**
 * Build the `{ earliest, latest }` time window for a stop as state-local,
 * timezone-aware datetimes. Returns empty strings when the stop has no date.
 */
function stopTimeRange(stop: BookingStop): {
  earliest: string;
  latest: string;
} {
  const startDate = stop.start_date ?? '';
  const endDate = stop.link_dates
    ? startDate
    : (stop.end_date ?? stop.start_date ?? '');
  if (!startDate) return { earliest: '', latest: '' };

  let earliestTime = '00:00:00';
  let latestTime = '00:00:00';
  if (stop.time_mode === 'ampm') {
    // The gap-adjusted window is stored on the stop (see clampStopsToMinTimes);
    // fall back to the raw preset when it has not been resolved yet.
    const preset =
      stop.am_pm === 'PM' ? STOP_DEFAULT_TIMES.PM : STOP_DEFAULT_TIMES.AM;
    earliestTime = stop.time_from
      ? formatTime(stop.time_from)
      : preset.earliest;
    latestTime = stop.time_to ? formatTime(stop.time_to) : preset.latest;
  } else if (stop.time_mode === 'specific') {
    earliestTime = formatTime(stop.time_from);
    latestTime = formatTime(stop.time_to);
  } else {
    // "any" — span the working day; warehouses open earlier than customers.
    const window = stop.warehouse
      ? STOP_DEFAULT_TIMES.anyWarehouse
      : STOP_DEFAULT_TIMES.anyCustomer;
    earliestTime = window.earliest;
    latestTime = window.latest;
  }

  const tzOffset = tzOffsetForState(stop.state, startDate);
  return {
    earliest: `${startDate}T${earliestTime}${tzOffset}`,
    latest: `${endDate}T${latestTime}${tzOffset}`,
  };
}

type BookApiItem = openapi.PostJobBookApi_Body[number];

function buildStop(stop: BookingStop): BookApiItem['stops'][number] {
  return {
    number: stop.number,
    name: stop.name,
    address_line1: stop.address_line1,
    address_line2: stop.address_line2,
    suburb: stop.suburb,
    state: stop.state,
    postcode: stop.postcode,
    warehouse: stop.warehouse,
    attributes: stop.attributes,
    note_name: stop.note_name,
    note_phone: stop.note_phone,
    note_text: stop.note_text,
    note_email: stop.note_email,
    time: stopTimeRange(stop),
  };
}

function buildProduct(
  product: BookingProduct,
  isReturnLeg: boolean,
): BookApiItem['products'][number] {
  return {
    serial: product.serial,
    model: product.model,
    model_id: product.model_id,
    description: product.description,
    quantity: product.quantity,
    weight: product.weight,
    from_stop: product.from_stop,
    to_stop: product.to_stop,
    installation: isReturnLeg ? '' : product.installation,
    runup: product.runup,
    returns: isReturnLeg ? product.returns : '',
    finisher: product.finisher,
  };
}

export function buildJobBookingItem(
  customer: JobBookingCustomer,
  stops: BookingStop[],
  products: BookingProduct[],
): BookApiItem {
  const isWarehouseStop = new Map(
    stops.map((stop) => [stop.number, stop.warehouse]),
  );
  return {
    schema_version: SCHEMA_VERSION,
    booked: new Date().toISOString(),
    customer_code: customer.customer_code ?? '',
    caller: customer.caller,
    phone: customer.phone,
    REF1: customer.REF1,
    REF2: customer.REF2,
    REF3: customer.REF3,
    stops: stops.map(buildStop),
    products: products.map((product) =>
      buildProduct(product, isWarehouseStop.get(product.to_stop) ?? false),
    ),
  };
}

export function buildBookApiBody(
  customer: JobBookingCustomer,
  stops: BookingStop[],
  products: BookingProduct[],
): openapi.PostJobBookApi_Body {
  return [buildJobBookingItem(customer, stops, products)];
}

export function bookApiFailureReasons(
  failed: readonly { error_reason?: string }[] | undefined,
): string[] {
  return (failed ?? [])
    .map((item) => item.error_reason)
    .filter((reason): reason is string => !!reason);
}

type DraftContent = Partial<BookApiItem>;
type DraftStop = Partial<BookApiItem['stops'][number]>;
type DraftProduct = Partial<BookApiItem['products'][number]>;

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

interface DraftCustomerFields {
  customer_code?: string | null;
  caller?: string;
  phone?: string;
  REF1?: string;
  REF2?: string;
  REF3?: string;
}

export function parseDraftCustomer(
  item: DraftCustomerFields,
): JobBookingCustomer {
  return {
    customer_code: item.customer_code ?? null,
    caller: item.caller ?? '',
    phone: item.phone ?? '',
    REF1: item.REF1 ?? '',
    REF2: item.REF2 ?? '',
    REF3: item.REF3 ?? '',
  };
}

interface DraftStopBaseFields {
  number?: number;
  name?: string;
  address_line1?: string;
  address_line2?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  warehouse?: boolean;
  attributes?: string[];
  note_name?: string;
  note_phone?: string;
  note_text?: string;
  note_email?: string;
}

type DraftStopBase = Omit<
  BookingStop,
  | 'start_date'
  | 'end_date'
  | 'link_dates'
  | 'time_mode'
  | 'am_pm'
  | 'time_from'
  | 'time_to'
>;

export function parseDraftStopBase(stop: DraftStopBaseFields): DraftStopBase {
  return {
    number: (stop.number ?? 1) as BookingStop['number'],
    name: stop.name ?? '',
    address_line1: stop.address_line1 ?? '',
    address_line2: stop.address_line2 ?? '',
    suburb: stop.suburb ?? '',
    suburb_id: null,
    state: stop.state ?? '',
    postcode: stop.postcode ?? '',
    warehouse: stop.warehouse ?? false,
    attributes: stop.attributes ?? [],
    note_name: stop.note_name ?? '',
    note_phone: stop.note_phone ?? '',
    note_text: stop.note_text ?? '',
    note_email: stop.note_email ?? '',
    save_to_address_book: false,
  };
}

/** Parse a state-local ISO (`2024-10-31T16:30:00+11:00`) into date + time-of-day. */
function parseStopDateTime(
  iso: unknown,
): { date: string; time: StopTimeObject } | null {
  if (typeof iso !== 'string') return null;
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/.exec(iso);
  if (!match) return null;
  return {
    date: match[1],
    time: { hours: Number(match[2]), minutes: Number(match[3]) },
  };
}

function stripLegPrefix(model: string): string {
  const lastIndex = model.lastIndexOf(': ');
  return lastIndex >= 0 ? model.slice(lastIndex + 2) : model;
}

type StopDateTime = { date: string; time: StopTimeObject } | null;

// Shared by parseJobBookingDraft and mapDetailedJobToBooking: both load a "specific" window from the source's earliest/latest timestamps.
function stopTimeWindowFields(
  from: StopDateTime,
  to: StopDateTime,
): Pick<
  BookingStop,
  | 'start_date'
  | 'end_date'
  | 'link_dates'
  | 'time_mode'
  | 'am_pm'
  | 'time_from'
  | 'time_to'
> {
  const hasWindow = from !== null && to !== null;
  return {
    start_date: from?.date ?? null,
    end_date: to?.date ?? from?.date ?? null,
    link_dates: hasWindow ? from.date === to.date : true,
    time_mode: hasWindow ? 'specific' : 'any',
    am_pm: null,
    time_from: from?.time ?? null,
    time_to: to?.time ?? null,
  };
}

/**
 * Inverse of `buildJobBookingItem`: map a saved draft's `book_api` content back
 * into the form models. The time window loads as a `specific` window
 */
export function parseJobBookingDraft(content: unknown): {
  customer: JobBookingCustomer;
  stops: BookingStop[];
  products: BookingProduct[];
} | null {
  if (!isRecord(content) || !Array.isArray(content.stops)) return null;
  const item = content as unknown as DraftContent;

  const customer = parseDraftCustomer(item);

  const stops: BookingStop[] = (item.stops ?? []).map((raw) => {
    const stop = raw as DraftStop;
    const from = parseStopDateTime(stop.time?.earliest);
    const to = parseStopDateTime(stop.time?.latest);
    return {
      ...parseDraftStopBase(stop),
      ...stopTimeWindowFields(from, to),
    };
  });

  const products: BookingProduct[] = (item.products ?? []).map((raw) => {
    const product = raw as DraftProduct;
    return {
      serial: product.serial ?? '',
      model: product.model ?? '',
      model_id: product.model_id ?? null,
      description: product.description ?? '',
      quantity: product.quantity ?? 1,
      weight: product.weight ?? 0,
      from_stop: (product.from_stop ?? 1) as BookingProduct['from_stop'],
      to_stop: (product.to_stop ?? 2) as BookingProduct['to_stop'],
      installation: product.installation ?? '',
      runup: product.runup ?? false,
      returns: product.returns ?? '',
      finisher: product.finisher ?? false,
      predefined_workshop: false,
      custom_workshop: false,
      predefined_workshop_tasks: [],
      custom_workshop_task: '',
    };
  });

  return { customer, stops, products };
}

/** Map a fetched detailed job into the Advanced JobBooking form models, for the clone flow. */
export function mapDetailedJobToBooking(job: openapi.JobOpenapi): {
  customer: JobBookingCustomer;
  stops: BookingStop[];
  products: BookingProduct[];
} {
  const customer: JobBookingCustomer = {
    customer_code: job.customer || null,
    caller: job.caller,
    phone: job.phone,
    REF1: job.reference_1,
    REF2: job.reference_2,
    REF3: job.reference_3,
  };

  const stops: BookingStop[] = job.stops.map((stop) => {
    const from = parseStopDateTime(stop.time_earliest);
    const to = parseStopDateTime(stop.time_latest);
    return {
      number: stop.stop_number as StopNumber,
      name: stop.name,
      address_line1: stop.address_line_1,
      address_line2: stop.address_line_2,
      suburb: stop.suburb,
      suburb_id: null,
      state: stop.state,
      postcode: stop.post_code,
      warehouse: stop.is_warehouse,
      attributes: stop.attributes,
      note_name: stop.note_name ?? '',
      note_phone: stop.note_phone ?? '',
      note_text: stop.note_text ?? '',
      note_email: stop.note_email ?? '',
      ...stopTimeWindowFields(from, to),
      save_to_address_book: false,
    };
  });

  // Products live on the stop where they are picked up; `delivery_stop_number` is where they land.
  const products: BookingProduct[] = [];
  job.stops.forEach((stop) => {
    Object.values(stop.pickup_products).forEach((product) => {
      products.push({
        serial: product.serial,
        model: stripLegPrefix(product.model),
        model_id: product.model_id,
        description: product.description ?? '',
        quantity: product.pieces,
        weight: product.weight ?? 0,
        from_stop: stop.stop_number as StopNumber,
        to_stop: product.delivery_stop_number as StopNumber,
        installation: (product.extra_options?.installation ??
          '') as BookingProduct['installation'],
        runup: product.extra_options?.runup ?? false,
        returns: (product.extra_options?.returns ??
          '') as BookingProduct['returns'],
        finisher: false,
        predefined_workshop: false,
        custom_workshop: false,
        predefined_workshop_tasks: [],
        custom_workshop_task: '',
      });
    });
  });

  return { customer, stops, products };
}
